import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ItemAction } from '../../types/orderActionSummary';
import { getSupportedOrderActions } from '../../apis/bopApi';
import useSiteData from '../../hooks/useSiteData';
import useIdentityContext from '../../hooks/useIdentityContext';

interface IOrderActionsContext {
  isLoading: boolean;
  isError: boolean;
  itemActions: ItemAction[];
  fetchOrderItemActions: () => Promise<void>;
  isItemSelfCancellable: (lineItemId: string) => boolean;
}

const defaultContext: IOrderActionsContext = {
  isLoading: true,
  isError: false,
  itemActions: [],
  // eslint-disable-next-line prettier/prettier
  fetchOrderItemActions: () => new Promise(() => { }),
  isItemSelfCancellable: () => false,
};

const OrderActionsContext = createContext<IOrderActionsContext>(defaultContext);

type OrderActionsProps = {
  children: React.ReactNode;
  orderNumber: string;
};

const OrderActionsProvider = ({ children, orderNumber }: OrderActionsProps) => {
  const { tenantEnvironment } = useSiteData();
  const { identity } = useIdentityContext();
  const { shopperId } = identity;
  const [isLoading, setIsLoading] = useState(defaultContext.isLoading);
  const [isError, setIsError] = useState(defaultContext.isError);
  const [itemActions, setItemActions] = useState(defaultContext.itemActions);

  const fetchOrderItemActions = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const actions = await getSupportedOrderActions({
        tenantEnvironment,
        orderNumber,
        shopperId,
      });
      setItemActions(actions);
    } catch (error) {
      setIsError(true);
    }

    setIsLoading(false);
  }, [tenantEnvironment, orderNumber, shopperId]);

  const isItemSelfCancellable = useCallback(
    (lineItemId: string): boolean => {
      for (const itemAction of itemActions) {
        if (itemAction.lineItemId === lineItemId) {
          return itemAction.selfCancellability.isSelfCancellable;
        }
      }
      return false;
    },
    [itemActions]
  );

  useEffect(() => {
    fetchOrderItemActions();
  }, [fetchOrderItemActions]);

  const value = useMemo<IOrderActionsContext>(
    () => ({
      isLoading,
      isError,
      itemActions: itemActions || [],
      fetchOrderItemActions,
      isItemSelfCancellable,
    }),
    [
      isLoading,
      isError,
      itemActions,
      fetchOrderItemActions,
      isItemSelfCancellable,
    ]
  );

  return (
    <OrderActionsContext.Provider value={value}>
      {children}
    </OrderActionsContext.Provider>
  );
};

export { OrderActionsContext };
export default OrderActionsProvider;
